import { useState } from 'react';
import { Box, Grid, Paper, Typography } from '@material-ui/core';
import { observer } from 'mobx-react';
import { useEffect } from 'react';
import { ProgressBarComponent, Button } from '@roc/ui';
import { LoanProcess } from '@roc/feature-utils';
import { TodoTaskCard } from '@roc/feature-loan-details';
import { AddDrawDocumentDialog } from '../common/addDrawDocumentDialog';
import AddIcon from '@material-ui/icons/Add';
import { useDocumentStore } from '@roc/feature-documents';
import {
  SOW_REVISION_STAGE,
  ON_HOLD,
  INSURANCE_DRAW,
} from '../constants/draws';
import { useDrawsStore } from '../hooks';
import { Alert, AlertTitle } from '@material-ui/lab';
import { alpha, makeStyles } from '@material-ui/core/styles';
import { DrawTodos } from './drawTodos';
import { DrawActionButtons } from 'libs/feature-loan-details/src/loanDetails/components/action-buttons/drawActionButtons';
import { useUserStore, useBaseStore } from '@roc/feature-app-core';
import { SELF_INSPECTION_MOBILE } from '../stores/drawActionsStore';
import { MobileSnapDrawAlert } from './mobileSnapDrawAlert';
import { MorganStanleyAlert } from './morganStanleyAlert';

const useStyles = makeStyles(theme => ({
  // Same look as the loan closing stepper (loanDetailsStepper.tsx); stages come from the backend, styling only
  stepper: {
    overflow: 'auto',
    '& .MuiStepper-root': {
      background: 'transparent',
    },
    // connector: thin light track, primary once reached
    '& .MuiStepConnector-alternativeLabel': {
      top: 12,
      left: 'calc(-50% + 20px)',
      right: 'calc(50% + 20px)',
    },
    '& .MuiStepConnector-lineHorizontal': {
      borderTopWidth: 2,
      borderColor: '#DADEF4',
    },
    '& .MuiStepConnector-active .MuiStepConnector-lineHorizontal, & .MuiStepConnector-completed .MuiStepConnector-lineHorizontal': {
      borderColor: theme.palette.primary.main,
    },
    // icons: 26px, no numbers
    '& .MuiStepIcon-root': {
      fontSize: 26,
      borderRadius: '50%',
    },
    '& .MuiStepIcon-text': {
      fill: 'transparent',
    },
    // upcoming: hollow outlined circle
    '& .MuiStepIcon-root:not(.MuiStepIcon-active):not(.MuiStepIcon-completed)': {
      color: '#FFFFFF',
      '& circle': {
        r: '11px',
        stroke: '#CED3F0',
        strokeWidth: 2,
      },
    },
    // current: primary ring + soft halo + center dot
    '& .MuiStepIcon-active': {
      color: theme.palette.primary.main,
      backgroundColor: '#FFFFFF',
      boxShadow: `inset 0 0 0 2px ${theme.palette.primary.main}, 0 0 0 4px ${alpha(theme.palette.primary.main, 0.15)}`,
      '& circle': {
        r: '5px',
      },
    },
    '& .MuiStepIcon-completed': {
      color: theme.palette.primary.main,
    },
    // labels: muted for upcoming, dark for done / current
    '& .MuiStepLabel-label': {
      marginTop: 8,
      color: theme.palette.text.secondary,
    },
    '& .MuiStepLabel-label.MuiStepLabel-active, & .MuiStepLabel-label.MuiStepLabel-completed': {
      color: theme.palette.text.primary,
      fontWeight: 500,
    },
  },
  button: {
    border: `1px solid rgba(40, 83, 145, 1)`,
    borderRadius: 4,
    backgroundColor: 'transparent',
    fontSize: 14,
    fontWeight: 500,
    color: 'rgba(40, 83, 145, 1)',
    padding: '4px 8px',
    marginLeft: 10,
    textTransform: 'capitalize',
    '&:hover': {
      backgroundColor: 'rgba(237, 237, 237, 1)',
    },
  },
  buttonModal: {
    display: 'flex',
    marginLeft: 'auto',
    marginBottom: 16,
  },
}));
export const DrawDetailsModalShared = observer(
  ({
    loanId,
    drawId,
    isCPortal,
  }: {
    loanId: string;
    drawId: string;
    isCPortal?: boolean;
  }) => {
    const classes = useStyles();
    const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
    const { drawStore } = useDrawsStore();
    const { documentStore } = useDocumentStore();
    const { userStore } = useUserStore();
    const { globalStore } = useBaseStore();
    const [investor, setInvestor] = useState();

    useEffect(() => {
      setInvestor(documentStore?.loanStore?.loanDetails?.investorName);
      drawStore.fetchDrawDTOById(drawId);
      return () => {
        drawStore.resetCurrentDraw();
        documentStore.resetDrawDocuments();
        documentStore.resetDrawComments();
      };
    }, []);

    useEffect(() => {
      if (drawStore.currentDraw) {
        drawStore.drawFormStore.onFieldChange(
          'drawId',
          drawStore.currentDraw.drawId
        );
        drawStore.drawFormStore.onFieldChange(
          'propertyId',
          drawStore.currentDraw.propertyId
        );
      }
    }, [drawStore.currentDraw]);

    if (!drawStore.currentDraw || !documentStore.drawDocuments) {
      return <></>;
    }

    const displayProgressBar =
      drawStore.currentDraw.stage !== SOW_REVISION_STAGE &&
      drawStore.currentDraw.stage !== INSURANCE_DRAW &&
      drawStore.currentDraw.stage !== ON_HOLD &&
      drawStore.currentDraw.stageArray?.length > 0;

    const borrowers = documentStore?.loanStore?.loanDetails?.borrowers ?? [];
    const isSnapDrawEligible = borrowers.every(
      b => b?.isEligibleForSnapDraws === true
    );
    const isDiscountedDraw = borrowers.some(b => b?.isDiscountedDraw === true);
    const isMobileDraw =
      drawStore.currentDraw.inspectionCompany === SELF_INSPECTION_MOBILE;

    return (
      <Grid container spacing={2}>
        {isDiscountedDraw && globalStore.isInternalPortal && (
          <Grid item xs={12}>
            <Alert severity="error">
              <AlertTitle>
                <strong>Discounted draw</strong>
              </AlertTitle>
              This borrower is eligible for the discounted draw category. Please ensure the fees are changed accordingly as per the new guidelines.
            </Alert>
          </Grid>
        )}
        {!isSnapDrawEligible && (
          <Grid item xs={12}>
            <Alert severity="warning">
              <AlertTitle>
                <strong>Snap Draws Disabled</strong>
              </AlertTitle>
              At least one borrower on this loan is not eligible for Snap Draws.
            </Alert>
          </Grid>
        )}
        {drawStore.currentDraw.stage === ON_HOLD && (
          <Grid item xs={12}>
            <Alert severity="warning">Draw Request is currently On Hold</Alert>
          </Grid>
        )}
        {displayProgressBar && (
          <Grid item xs={12}>
            <Box className={classes.stepper}>
              <ProgressBarComponent
                progressArray={drawStore.currentDraw.stageArray}
                active={drawStore.currentDraw.stage}
              />
            </Box>
          </Grid>
        )}
        {(
          <Grid item xs={12}>
            <MobileSnapDrawAlert
              isMobileDraw={isMobileDraw}
              investor={investor}
              lenderId={documentStore?.loanStore?.loanDetails?.lenderId}
            />
          </Grid>
        )}
        {userStore.isNportalUser && (
          <Grid item xs={12}>
            <DrawActionButtons loanId={+loanId} drawId={+drawId} />
          </Grid>
        )}
        <Grid item xs={12}>
          <DrawTodos isCPortal={isCPortal} loanId={+loanId} drawId={+drawId} />
        </Grid>
        <Grid item xs={12}>
          <Box style={{ height: '30px' }} />
        </Grid>
      </Grid>
    );
  }
);

export default DrawDetailsModalShared;
